/* HYV: rev 1791000001 */
/**
 * HYV Product Card JavaScript
 * File: assets/hyv-product-card.js
 *
 * Handles:
 *   - Quick add to cart (single + multi-variant)
 *   - Color swatch variant switching (image + price update)
 *   - Capacity pill selection
 *   - Wishlist (localStorage placeholder, ready for app hook)
 *
 * No jQuery. Vanilla JS only. < 5KB minified.
 */

(function () {
  'use strict';

  /* ── UTILITIES ──────────────────────────────────── */
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ── QUICK ADD ──────────────────────────────────── */
  async function addToCart(variantId, btn) {
    if (!variantId) return;

    /* Restore the button's own label afterwards. It used to come back as a
       bare "+" icon, which undid the labelled "Add" button. */
    if (!btn.dataset.label) btn.dataset.label = btn.innerHTML;
    btn.classList.add('is-adding');
    btn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!res.ok) throw new Error('Cart add failed');

      btn.classList.remove('is-adding');
      btn.classList.add('is-added');

      /* Swap icon to checkmark */
      btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

      /* Trigger Impact theme cart update (slide-out cart) */
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      /* Also try Impact's built-in cart open */
      if (window.theme && window.theme.cart) {
        window.theme.cart.open();
      }

      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
        btn.innerHTML = btn.dataset.label;
      }, 2200);

    } catch (err) {
      console.error('[HYV Card] Cart add error:', err);
      btn.classList.remove('is-adding');
      btn.disabled = false;
      btn.innerHTML = btn.dataset.label;
    }
  }

  /* ── SWATCH VARIANT SWITCHING ───────────────────── */
  function initSwatchSwitching(card) {
    const swatches = qsa('.hyv-card__swatch', card);
    if (!swatches.length) return;

    const primaryImg = qs('.hyv-card__image--primary img', card);

    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        swatches.forEach(s => s.classList.remove('hyv-card__swatch--active'));
        swatch.classList.add('hyv-card__swatch--active');

        const variantId  = swatch.dataset.variantId;
        const variantUrl = swatch.dataset.variantUrl;
        const color      = swatch.dataset.color;

        syncCardToVariant(card, variantId, variantUrl);

        /* One /variants fetch drives both the image and the price. */
        if (variantId) applyVariant(variantId, primaryImg, card);
      });
    });
  }

  /* A tapped colour must follow the shopper everywhere the card leads.
     Before 16 Oct 2026 only the title link and the single-variant Add learnt
     the new variant: the IMAGE link (the biggest tap target on the card) still
     opened the default colour, and the multi-variant Add opened Impact's
     quick-buy drawer on the default colour too, so "Charcoal Grey" tapped on
     the card became "Indigo Blue" in the drawer. The drawer reads its
     `handle` attribute (handle?variant=ID) only on first open, so it is
     rewritten here and its loaded flag cleared to force a fresh fetch. */
  function syncCardToVariant(card, variantId, variantUrl) {
    if (!variantId) return;
    if (variantUrl) {
      qsa('.hyv-card__title a, .hyv-card__image-link', card).forEach(a => { a.href = variantUrl; });
    }
    const addBtn = qs('.hyv-card__add-btn', card);
    if (addBtn) addBtn.dataset.variantId = variantId;
    const qb = qs('quick-buy-drawer', card);
    if (qb) {
      const handle = (qb.getAttribute('handle') || '').split('?')[0];
      if (handle) {
        qb.setAttribute('handle', `${handle}?variant=${variantId}`);
        qb._hasLoaded = false;
      }
    }
  }

  const variantCache = {};
  function getVariant(variantId) {
    if (!variantCache[variantId]) {
      variantCache[variantId] = fetch(`/variants/${variantId}.js`)
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return variantCache[variantId];
  }

  async function applyVariant(variantId, primaryImg, card) {
    const variant = await getVariant(variantId);
    if (!variant) return;
    if (variant.featured_image && primaryImg) {
      primaryImg.removeAttribute('srcset');
      primaryImg.src = shopifyImgUrl(variant.featured_image.src, 600);
      if (variant.featured_image.alt) primaryImg.alt = variant.featured_image.alt;
    }
    renderVariantPrice(variant, card);
  }

  function shopifyImgUrl(src, width) {
    if (!src) return src;
    /* Handles both legacy ("_600x.jpg") and modern ("?width=600") CDN URL formats. */
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, `$1width=${width}`);
    return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
  }

  function renderVariantPrice(variant, card) {
    const priceEl   = qs('.hyv-card__price:not(.hyv-card__price--compare)', card);
    const compareEl = qs('.hyv-card__price--compare', card);

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      priceEl.classList.toggle('hyv-card__price--sale', variant.compare_at_price > variant.price);
    }

    /* MRP stays visible whenever the variant has one (Legal Metrology); the
       "MRP " prefix is CSS, so only the amount is written here. */
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) compareEl.textContent = formatMoney(variant.compare_at_price);
      else {
        const prices = qs('.hyv-card__prices', card);
        if (prices) {
          const el = document.createElement('span');
          el.className = 'hyv-card__price hyv-card__price--compare';
          el.textContent = formatMoney(variant.compare_at_price);
          prices.appendChild(el);
        }
      }
    } else if (compareEl) {
      compareEl.remove();
    }
  }

  function formatMoney(cents) {
    /* Shopify money formatting — adjust currency symbol/format here */
    const amount = (cents / 100).toFixed(0);
    return `₹${parseInt(amount).toLocaleString('en-IN')}`;
  }

  /* ── WISHLIST ────────────────────────────────────── */
  function initWishlist(card) {
    const btn = qs('.hyv-card__wishlist-btn', card);
    if (!btn) return;

    const productId = btn.dataset.productId;
    if (!productId) return;

    const saved = getSavedItems();
    if (saved.includes(productId)) {
      btn.classList.add('is-saved');
    }

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const items = getSavedItems();
      const idx   = items.indexOf(productId);

      if (idx === -1) {
        items.push(productId);
        btn.classList.add('is-saved');
      } else {
        items.splice(idx, 1);
        btn.classList.remove('is-saved');
      }

      localStorage.setItem('hyv_wishlist', JSON.stringify(items));

      /* Dispatch for potential app integrations */
      document.dispatchEvent(new CustomEvent('hyv:wishlist:change', {
        detail: { productId, saved: idx === -1 }
      }));
    });
  }

  function getSavedItems() {
    try {
      return JSON.parse(localStorage.getItem('hyv_wishlist') || '[]');
    } catch { return []; }
  }

  /* Multi-variant card buttons use Impact's native <quick-buy-drawer> via
     is="custom-button" + aria-controls. No JS needed here — Impact handles it. */

  /* ── SINGLE VARIANT QUICK ADD ────────────────────── */
  function initSimpleAddBtn(card) {
    const btn = qs('.hyv-card__add-btn:not(.hyv-card__add-btn--options):not(.hyv-card__add-btn--sold-out)', card);
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      await addToCart(variantId, btn);
    });
  }

  /* ── FILTER-AWARE SWATCH PRESELECT ───────────────── */
  /* When the page URL includes an active color filter, auto-click the matching
     swatch on each card so the image + variant URL reflect the filtered colour.

     Two filter param shapes are supported:
     1) filter.v.option.color=Cashmere+Beige   — exact HYV variant name
     2) filter.v.t.shopify.color-pattern=gid://shopify/TaxonomyValue/6
        — broad Shopify taxonomy color (Beige / Blue / Pink / …). We translate
          the GID/label to a list of matching HYV variant names. */

  const BROAD_TO_HYV = {
    'beige':  ['cashmere beige'],
    'black':  ['midnight black'],
    'blue':   ['indigo blue', 'sapphire blue', 'cobalt blue', 'glacier blue', 'ocean blue', 'crystal aqua', 'cool mint'],
    'brown':  ['mocha mint'],
    'gray':   ['charcoal grey', 'platinum grey', 'stone haze', 'mirror chrome'],
    'grey':   ['charcoal grey', 'platinum grey', 'stone haze', 'mirror chrome'],
    'green':  ['sage green', 'forrest green', 'spear mint', 'matcha mist'],
    'orange': ['sunset orange'],
    'pink':   ['powder pink', 'coral pink', 'electric pink', 'candy floss', 'dusty rose', 'pink marshmallow', 'berry cream'],
    'purple': ['royal lavender', 'serene lavender', 'misty lilac', 'purple haze', 'grape soda'],
    'silver': ['mirror chrome'],
    'white':  ['arctic white']
  };

  function gidLabelMap() {
    /* Read the sidebar filter inputs to map taxonomy GIDs to their labels,
       so we don't need to hardcode the GID numbers (they can change). */
    const map = {};
    document.querySelectorAll('input[name="filter.v.t.shopify.color-pattern"]').forEach(inp => {
      const lbl = inp.closest('label');
      if (!lbl) return;
      const text = lbl.textContent.replace(/\s*\(\s*\d+\s*\)\s*/, '').trim().toLowerCase();
      if (text) map[inp.value] = text;
    });
    return map;
  }

  function getActiveColorFilters() {
    const params = new URLSearchParams(window.location.search);
    const colors = new Set();

    /* Direct option filter (specific HYV color name) */
    ['filter.v.option.color', 'filter.v.option.colour'].forEach(k => {
      params.getAll(k).forEach(v => colors.add(v.toLowerCase()));
    });

    /* Shopify taxonomy broad color filter */
    const gidMap = gidLabelMap();
    ['filter.v.t.shopify.color-pattern'].forEach(k => {
      params.getAll(k).forEach(raw => {
        const broad = gidMap[raw] || raw.toLowerCase();
        const list = BROAD_TO_HYV[broad];
        if (list) list.forEach(c => colors.add(c));
      });
    });

    return Array.from(colors);
  }

  function applyColorFilterToCard(card) {
    const filterColors = getActiveColorFilters();
    if (!filterColors.length) return;
    if (card.dataset.filterColor && filterColors.includes(card.dataset.filterColor)) return; /* already in sync */
    const swatches = qsa('.hyv-card__swatch', card);
    if (!swatches.length) return;
    for (const fc of filterColors) {
      const match = swatches.find(s => (s.dataset.color || '').toLowerCase() === fc);
      if (!match) continue;

      card.dataset.filterColor = fc;

      /* If the matched swatch is hidden in the overflow, promote it to the visible row */
      if (match.hasAttribute('hidden')) {
        const container = match.parentElement;
        match.removeAttribute('hidden');
        match.removeAttribute('aria-hidden');
        match.removeAttribute('tabindex');
        match.classList.remove('hyv-card__swatch--overflow');
        if (container && container.firstChild !== match) {
          container.insertBefore(match, container.firstChild);
        }
        const visibleSwatches = qsa('.hyv-card__swatch:not([hidden])', container);
        if (visibleSwatches.length > 8) {
          const demoted = visibleSwatches[visibleSwatches.length - 1];
          demoted.setAttribute('hidden', '');
          demoted.setAttribute('aria-hidden', 'true');
          demoted.setAttribute('tabindex', '-1');
          demoted.classList.add('hyv-card__swatch--overflow');
        }
      }

      /* Apply swatch state DIRECTLY — don't rely on .click() being wired up yet
         (initCard order is fine on first load but AJAX re-renders are racier). */
      swatches.forEach(s => s.classList.remove('hyv-card__swatch--active'));
      match.classList.add('hyv-card__swatch--active');

      const variantId = match.dataset.variantId;
      syncCardToVariant(card, variantId, match.dataset.variantUrl);
      if (variantId) applyVariant(variantId, qs('.hyv-card__image--primary img', card), card);
      return;
    }
  }

  /* ── CARD INIT ───────────────────────────────────── */
  function initCard(card) {
    initSwatchSwitching(card);
    initWishlist(card);
    initSimpleAddBtn(card);
    applyColorFilterToCard(card);
  }

  /* ── OBSERVER for dynamic card injection ─────────── */
  function observeGrid() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('hyv-card')) initCard(node);
          qsa('.hyv-card', node).forEach(initCard);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ── URL CHANGE WATCHER (handles AJAX filter updates) ─ */
  /* Impact's <facet-form> updates the grid via AJAX + history.pushState.
     The MutationObserver above sees the new cards but applyColorFilterToCard
     can race the pushState. This poller catches URL changes and re-applies
     the active filter to all current cards. */
  function reapplyFiltersToAllCards() {
    qsa('.hyv-card').forEach(card => {
      delete card.dataset.filterColor;
      applyColorFilterToCard(card);
    });
  }

  function watchUrlChanges() {
    let lastSearch = window.location.search;
    setInterval(() => {
      if (window.location.search !== lastSearch) {
        lastSearch = window.location.search;
        setTimeout(reapplyFiltersToAllCards, 50);
      }
    }, 100);
    window.addEventListener('popstate', () => {
      setTimeout(reapplyFiltersToAllCards, 50);
    });

    /* Monkeypatch pushState to catch URL updates the moment they happen */
    const origPush = history.pushState;
    history.pushState = function () {
      const ret = origPush.apply(this, arguments);
      setTimeout(reapplyFiltersToAllCards, 50);
      return ret;
    };

    /* Listen on the facet form itself — when any filter input changes,
       schedule multiple re-applies to catch the AJAX response whenever it lands. */
    document.addEventListener('change', (e) => {
      const form = e.target && e.target.closest && e.target.closest('#facet-form, [is="facet-form"]');
      if (!form) return;
      [200, 500, 1000].forEach(d => setTimeout(reapplyFiltersToAllCards, d));
    });
  }

  /* ── BOOTSTRAP ───────────────────────────────────── */
  function init() {
    qsa('.hyv-card').forEach(initCard);
    observeGrid();
    watchUrlChanges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();